import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, PageHead, useToast } from '@w7/ui'
import { sessionApi } from '@/api/sessionApi'
import {
  displayName,
  displayResult,
  formatResultOddsOption,
  parseDeclaredBoardResponse,
  ratesForResultFancy,
  runners,
  type DeclaredSub,
  type ResultRateOpt,
  type ResultRunOpt,
} from './resultListUtils'

type MarketOpt = { _id: string; name: string; level?: string }

const STORE_KEY = 'storeSelectedMarket'

export function ResultListPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const [list, setList] = useState<DeclaredSub[]>([])
  const [runList, setRunList] = useState<ResultRunOpt[]>([])
  const [rateList, setRateList] = useState<ResultRateOpt[]>([])
  const [markets, setMarkets] = useState<MarketOpt[]>([])
  const [marketId, setMarketId] = useState('')
  const [selectMarket, setSelectMarket] = useState<MarketOpt | null>(null)

  const filteredMatches = useMemo(
    () => markets.filter((m) => !m.level || m.level === 'event'),
    [markets],
  )

  const loadDeclared = (market: MarketOpt | null) => {
    if (!market?._id) {
      setMarketId('')
      setSelectMarket(null)
      setList([])
      setRunList([])
      setRateList([])
      return
    }
    setMarketId(market._id)
    setSelectMarket(market)
    localStorage.setItem(STORE_KEY, JSON.stringify(market))
    void sessionApi
      .getAllResultDeclared(market._id)
      .then((res) => {
        // Prod ResultScoreBoard: rateList/runList come from getAllResultDeclared.
        const board = parseDeclaredBoardResponse(res.data)
        setList(board.list)
        setRunList(board.runList)
        setRateList(board.rateList)
      })
      .catch(() => {
        setList([])
        setRunList([])
        setRateList([])
        toast.error('Failed to load declared results')
      })
  }

  useEffect(() => {
    void sessionApi
      .getAllMarketsForResults()
      .then((res) => {
        const data = res.data as MarketOpt[] | { data?: MarketOpt[] }
        const all = Array.isArray(data) ? data : (data?.data ?? [])
        const list = Array.isArray(all) ? all : []
        setMarkets(list)
        const events = list.filter((m) => !m.level || m.level === 'event')
        let initial: MarketOpt | null = null
        try {
          const raw = localStorage.getItem(STORE_KEY)
          if (raw) {
            const stored = JSON.parse(raw) as MarketOpt
            initial = events.find((m) => m._id === stored._id) ?? null
          }
        } catch {
          /* ignore */
        }
        if (!initial && events[0]) initial = events[0]
        if (initial) loadDeclared(initial)
      })
      .catch(() => toast.error('Failed to load markets'))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [toast])

  return (
    <div>
      <PageHead title="Results" onBack={() => navigate('/sessionmarket')} />
      <div className="sm-row" style={{ marginBottom: 12 }}>
        <select
          className="w7-input"
          value={marketId}
          onChange={(e) => {
            const m = filteredMatches.find((x) => x._id === e.target.value) ?? null
            loadDeclared(m)
          }}
        >
          <option value="">Select Market</option>
          {filteredMatches.map((m) => (
            <option key={m._id} value={m._id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>
      <div className="sm-card">
        {selectMarket ? (
          <div className="sm-row" style={{ marginBottom: 12 }}>
            <strong className="sm-market-title">Market Name</strong>
            <Input value={selectMarket.name} readOnly style={{ flex: 1 }} />
          </div>
        ) : null}
        {!list.length ? (
          <h3 style={{ fontWeight: 700, paddingTop: 16 }}>
            {marketId ? 'NO RESULT FOUND' : 'Please Choose Market To see SubMarket'}
          </h3>
        ) : (
          list.map((row, i) => {
            const r = runners(row)
            const result = displayResult(row)
            // Lab ResultScoreBoard: full rateList (all presets), wrap like Runs.
            const oddsStrip = ratesForResultFancy(row, rateList)
            return (
              <div
                key={row._id ?? row.marketId ?? i}
                className="sm-result-board"
              >
                <div className="sm-row" style={{ justifyContent: 'space-between' }}>
                  <strong>{displayName(row)}</strong>
                </div>
                <div className="sm-row" style={{ marginTop: 8, gap: 24 }}>
                  <span>
                    Runs {r.runner1}/{r.runner2}
                  </span>
                  <span>Rate {r.ratesNo}</span>
                  <span>Result {result}</span>
                </div>
                <div className="sm-row sm-result-preset-row" style={{ marginTop: 10 }}>
                  <span className="sm-result-preset-label">Runs</span>
                  <Input value={String(r.runner1)} readOnly style={{ width: 72, flex: '0 0 auto' }} />
                  <Input value={String(r.runner2)} readOnly style={{ width: 72, flex: '0 0 auto' }} />
                  <div className="sm-result-wrap" role="list" aria-label="Runs options">
                    {runList.map((run) => {
                      const active = Number(r.runner2) - Number(r.runner1) === Number(run.runs)
                      return (
                        <button
                          key={run._id ?? String(run.runs)}
                          type="button"
                          className={`sm-run-btn${active ? ' active' : ''}`}
                          disabled
                        >
                          {run.runs}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div className="sm-row sm-chip-row" style={{ marginTop: 8 }}>
                  <span className="sm-chip-label">Rate</span>
                  <Input value={r.ratesNo} readOnly style={{ width: 100, flex: '0 0 auto' }} />
                  <div className="sm-chip-scroll" role="list" aria-label="Rate options">
                    {oddsStrip.map((rate, ri) => {
                      const oddsNo = formatResultOddsOption(rate)
                      const active = r.ratesNo === oddsNo
                      return (
                        <button
                          key={rate._id ?? `${oddsNo}-${ri}`}
                          type="button"
                          className={`sm-run-btn${active ? ' active' : ''}`}
                          disabled
                        >
                          {oddsNo}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
